import React, { useMemo } from 'react';
import { NativeSyntheticEvent, StyleProp, TextInput, TextInputKeyPressEventData, View, ViewStyle } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Hoverable } from 'react-native-web-hover';
import TooltipWrapper from '../../TooltipWrapper';
import inputStylesheet from '../../../styles/inputStyles';

interface Props {
    label?: string;
    value: string;
    placeholder?: string;
    onChange: (text: string) => void;
    onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
    showIcon?: boolean;
    style?: StyleProp<ViewStyle>;
    toUppercase?: boolean;
    maxCharacters?: number;
    disabled?: boolean;
    showTooltip?: boolean;
    tooltipText?: string;
    onTooltipHovered?: (hovered: boolean) => void;
}

function FilterInput({ label, value, onChange, placeholder, onKeyPress, showIcon = true, style, toUppercase, maxCharacters, disabled, showTooltip, tooltipText, onTooltipHovered }: Props) {
    const { styles, theme } = useStyles(stylesheet);
    const { styles: defaultInputStyles } = useStyles(inputStylesheet);

    const isPlaceholder = useMemo(() => !value || value.length === 0, [value]);

    return (
        <View style={[styles.inputContainer, style]} >
            {label ? <View style={styles.tooltipWrapper}>
                <MyAppText style={styles.filterText}>{label}</MyAppText>
                {showTooltip && tooltipText && <Hoverable>
                    {({ hovered }) => (
                        <TooltipWrapper
                            hovered={hovered}
                            onTooltipHovered={onTooltipHovered}
                            tooltipText={tooltipText}
                        />
                    )}
                </Hoverable>}
            </View>
                : null}
            <View style={styles.inputWrapper} pointerEvents={disabled ? 'none' : 'auto'}>
                {showIcon && (
                    <FontAwesomeIcon
                        icon={faSearch as IconDefinition}
                        fontSize={16}
                        color={theme.colors.placeholder}
                        style={styles.searchIcon}
                    />
                )}
                <TextInput
                    style={[defaultInputStyles.input({
                        placeholder: isPlaceholder,
                        disabled,
                    }), showIcon ? { paddingLeft: 40 } : {}]}
                    editable={!disabled}
                    value={value}
                    onChangeText={(value) => {
                        if (toUppercase) {
                            value = value.toUpperCase();
                        }
                        if (maxCharacters) {
                            value = value.slice(0, maxCharacters);
                        }

                        onChange(value);
                    }}
                    placeholder={placeholder}
                    placeholderTextColor={theme.colors.placeholder}
                    onKeyPress={onKeyPress}
                />
            </View>
        </View>
    );
}

const stylesheet = createStyleSheet(theme => ({
    inputContainer: {
        minWidth: 200,
    },
    filterText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        height: 25,
    },
    inputWrapper: {
        position: 'relative',
        justifyContent: 'center',
    },
    searchIcon: {
        position: 'absolute',
        left: 14,
        zIndex: 1,
    },
    disabled: {
        opacity: 0.4,
        backgroundColor: theme.colors.field.border,
    },
    tooltipWrapper: {
        flexDirection: 'row',
        gap: 5,
        alignItems: 'center',
        zIndex: 2
    },
    filterInput: (isPlaceholder: boolean, hasIcon: boolean) => ({
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderWidth: 1,
        borderRadius: 4,
        borderColor: theme.colors.field.border,
        height: 40,
        minHeight: 40,
        fontSize: 16,
        color: isPlaceholder ? theme.colors.placeholder : theme.colors.field.text,
        paddingLeft: hasIcon ? 35 : 10,
        paddingRight: 10,
    }),
}));

export default FilterInput;
